/* Copyright (c) 2020-present CloudIO Inc */

import { hasAutoLayoutParent } from 'componentregistry/utils/ManagerUtils';
import { googleMapType } from 'componentregistry/registry/constants';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './GoogleMap';
import { page as SamplePage } from '../../help/sample_pages/GoogleMapPage';
import { isEmpty, isNotEmpty } from '../../utils/isEmpty';
import { getName } from '../../registry/Manager';

const GoogleMapManager: ComponentManagerFn<'GoogleMap'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'GoogleMap'>(platform, 'GoogleMap'),
    type: 'GoogleMap',
    name: 'Google Map',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (!(['TriggerOnClick', 'InfoBox'] as ItemType[]).includes(childType))
        return false;
      if (childType === 'InfoBox') {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added`;
      }
      return true;
    },
    defaultValue,
    props: {
      mapHeight: {
        label: 'Height',
        type: 'number',
        min: 0,
        max: 800,
        step: 40,
      },
      mapType: {
        label: 'Map Type',
        type: 'select',
        options: googleMapType,
      },
      showFullScreenControl: {
        label: 'Show Full Screen Control',
        type: 'boolean',
      },
      showRotateControl: {
        label: 'Show Rotate Control',
        type: 'boolean',
      },
      showScaleControl: {
        label: 'Show Scale Control',
        type: 'boolean',
      },
      showStreetViewControl: {
        label: 'Show Screen View Control',
        type: 'boolean',
      },
      showMapTypeControl: {
        label: 'Show Map Type Control',
        type: 'boolean',
      },
      showZoomControl: {
        label: 'Show Zoom Control',
        type: 'boolean',
      },
      zoomOnScrollWheel: {
        label: 'Zoom On Scroll wheel',
        type: 'boolean',
      },
      showClusters: {
        label: 'Show Clusters',
        type: 'boolean',
      },
      minClusterSize: {
        label: 'Min Cluster Size',
        type: 'numberInput',
      },
      clusterSize: {
        label: 'Cluster Size',
        type: 'numberInput',
      },
      defaultZoomLevel: {
        label: 'Default Zoom Level',
        type: 'number',
        min: 0,
        max: 22,
        step: 1,
      },
      minZoom: {
        label: 'Min Zoom',
        type: 'number',
        min: 0,
        max: 22,
        step: 1,
      },
      maxZoom: {
        label: 'Max Zoom',
        type: 'number',
        min: 0,
        max: 22,
        step: 1,
      },
      tiltAngle: {
        label: 'Tilt Angle',
        type: 'numberInput',
      },
      referenceLatitude: {
        label: 'Reference Latitude',
        type: 'numberInput',
      },
      referenceLongitude: {
        label: 'Reference Longitude',
        type: 'numberInput',
      },
      src: {
        label: 'Marker Image Src',
        type: 'expression',
        returnType: async () => 'string',
      },
      markerIcon: {
        label: 'Marker Image',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      markerIconHeight: {
        label: 'Marker Image Height',
        type: 'number',
        min: 0,
        max: 100,
        step: 4,
      },
      markerIconWidth: {
        label: 'Marker Image Width',
        type: 'number',
        min: 0,
        max: 100,
        step: 4,
      },
      markerIconOriginX: {
        label: 'Marker Origin X',
        type: 'numberInput',
      },
      markerIconOriginY: {
        label: 'Marker Origin Y',
        type: 'numberInput',
      },
      latitude: {
        label: 'Latitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Decimal', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      longitude: {
        label: 'Longitude',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Decimal', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      markerLabel: {
        label: 'Marker Label',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'clusterSize':
        case 'minClusterSize':
          return itemData.showClusters;
        case 'mapHeight': {
          const page = platform.getPage(context.appUid, context.pageId);
          return hasAutoLayoutParent(page, page[context.itemId]).result;
        }
        case 'markerIconHeight':
        case 'markerIconWidth':
          return (
            isNotEmpty(itemData.src.value) || isNotEmpty(itemData.markerIcon)
          );
        case 'markerIcon':
          return isEmpty(itemData.src.value);
        case 'src':
          return isEmpty(itemData.markerIcon);
        default:
          return true;
      }
    },
    getDisplayText: (item) => '',
    category: 'Maps',
    layout: 'sizeAware',
    icon: { iconName: 'map' },
    description: `**Overview**<br/>
The Google Map component allows you to embed fully interactive Google Maps into your application. Similar to the Bing Map component, it enables the visualization of geospatial data by plotting markers from a Data Store onto a map surface. It supports standard Google Maps features like Street View, satellite imagery, and marker clustering.
Use this component when your users are familiar with the Google Maps interface or when you specifically require Google's points-of-interest data and Street View capabilities.

**1. Map Configuration**<br/>
You can finely tune the initial appearance and behavior of the map to match your application's focus.
* **Map Type:** Select the visual style, such as **Roadmap**, **Satellite**, **Hybrid**, or **Terrain**.
* **Positioning:** Define the starting viewpoint using **Reference Latitude** and **Reference Longitude**.
* **Zoom:** Set the **Default Zoom Level** (0-22). You can also restrict navigation by defining **Min Zoom** and **Max Zoom**.
* **Tilt & Rotation:** Use **Tilt Angle** and **Show Rotate Control** to enable 45-degree perspective views (available in specific metropolitan areas).

**2. User Interface Controls**<br/>
The component offers extensive options to toggle standard on-screen controls for a tailored user experience:
* **Navigation:** Enable **Show Zoom Control**, **Show Scale Control**, and **Zoom On Scroll Wheel**.
* **View Modes:** Use **Show Map Type Control** to let users switch layers and **Show Full Screen Control** for immersive viewing.
* **Street View:** Toggle **Show Street View Control** to allow users to drop the "Pegman" onto the map for street-level imagery and 360-degree navigation.

**3. Data Visualization (Markers)**<br/>
To display data, bind the component to a Data Store containing your geographic records.
* **Coordinates:** Connect **Latitude** and **Longitude** attributes (Decimal/Double) to your data fields to plot markers.
* **Labels:** Bind **Marker Label** to display identifying text directly on the markers.
* **Custom Icons:** * **Static Image:** Use **Marker Image Src** to set a single custom icon (URL) for all markers.
    * **Dynamic Image:** Use **Marker Image (attribute)** to pull different icon URLs from your database per record.
* **Dimensions:** Control icon rendering using **Marker Image Height**, **Width**, and **Origin (X/Y)** properties.

**4. Clustering**<br/>
* **Show Clusters:** Automatically groups nearby markers into a single cluster icon to prevent map clutter when viewing high-density datasets.
* **Cluster Size:** Configure the **Cluster Size** and **Min Cluster Size** to control exactly when markers should merge into a cluster.

**5. Interactivity**<br/>
* **InfoBox:** You can add an **InfoBox** child component to display detailed information popups when a marker is clicked.
* **Triggers:** Supports **TriggerOnClick** to initiate actions, such as navigating to a detail page or updating global variables, when a user interacts with the map surface or markers.

**6. Best Use Cases**<br/>
* **Real Estate:** Showing property locations with the ability for buyers to drop into **Street View** to inspect the neighborhood virtually.
* **Logistics:** Tracking delivery points across a city with clustering enabled to manage high-density urban areas.
* **Field Services:** Visualizing service requests where **Satellite imagery** helps technicians identify specific building access points or rooftop equipment.
* **Store Locators:** Helping customers find the nearest branch using familiar Google Maps navigation and points-of-interest data.`,
    helpRenders: [
      {
        title: 'Basic Google Map',
        description:
          'Google Map rendered with default properties. Change these map options to render the map with specific configuration',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 300,
        visibleProps: [
          'showFullScreenControl',
          'showMapTypeControl',
          'showScaleControl',
          'showStreetViewControl',
          'showZoomControl',
        ],
        item: {
          children: [],
          itemId: 'googleMap',
          parent: 'flexItem',
          props: {
            showFullScreenControl: true,
            referenceLatitude: 37.4048,
            referenceLongitude: -121.98777,
            zoomOnScrollWheel: false,
            defaultZoomLevel: 11,
          },
          type: 'GoogleMap',
        },
      },
      {
        title: 'Zoom',
        description:
          'Specify the values for these properties to set the zoom level for the map',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        height: 300,
        visibleProps: [
          'defaultZoomLevel',
          'minZoom',
          'maxZoom',
          'zoomOnScrollWheel',
        ],
        item: {
          children: [],
          itemId: 'googleMap',
          parent: 'flexItem',
          props: {
            zoomOnScrollWheel: false,
            referenceLatitude: 37.4048,
            referenceLongitude: -121.98777,
            defaultZoomLevel: 10,
          },
          type: 'GoogleMap',
        },
      },
      {
        title: 'Markers',
        description:
          'Specify the values for these properties to make changes to the markers',
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        height: 300,
        visibleProps: [
          'markerIconHeight',
          'markerIconWidth',
          'markerIconOriginX',
          'markerIconOriginY',
          'src',
        ],
        item: {
          children: [],
          itemId: 'googleMap',
          parent: 'flexItem',
          props: {
            latitude: 'latitude',
            longitude: 'longitude',
            markerLabel: 'city',
            referenceLatitude: 45,
            referenceLongitude: -86,
            zoomOnScrollWheel: false,
            defaultZoomLevel: 5,
            src: {
              type: 'text',
              value:
                'https://developers.google.com/maps/documentation/javascript/examples/full/images/beachflag.png',
            },
          },
          type: 'GoogleMap',
        },
      },
      {
        title: 'Center',
        description:
          'Specify the values for these properties to make change to center of the map. Eg: referenceLatitude: 45, referenceLongitude: -86',
        page: SamplePage,
        exampleId: 'e4',
        clearParentChildren: true,
        height: 300,
        visibleProps: ['referenceLatitude', 'referenceLongitude'],
        item: {
          children: [],
          itemId: 'googleMap',
          parent: 'flexItem',
          props: {
            referenceLatitude: 37.4048,
            referenceLongitude: -121.98777,
            zoomOnScrollWheel: false,
            defaultZoomLevel: 10,
          },
          type: 'GoogleMap',
        },
      },
      {
        title: 'Clustering',
        description:
          'Specify the values for these properties to display the markers as clusters',
        page: SamplePage,
        exampleId: 'e5',
        clearParentChildren: true,
        height: 300,
        visibleProps: ['showClusters', 'minClusterSize', 'clusterSize'],
        item: {
          children: [],
          itemId: 'googleMap',
          parent: 'flexItem',
          props: {
            latitude: 'latitude',
            longitude: 'longitude',
            referenceLatitude: 45,
            referenceLongitude: -86,
            zoomOnScrollWheel: false,
            defaultZoomLevel: 5,
          },
          type: 'GoogleMap',
        },
      },
      {
        title: 'Tilt, Rotation',
        description:
          'Specify the values for these properties to tilt or rotate the map. Note: you can only display that in conjunction with the rotate control. you will only see the rotate controls in areas with 45 degree imagery (city centres etc)',
        page: SamplePage,
        exampleId: 'e6',
        clearParentChildren: true,
        height: 320,
        visibleProps: ['tiltAngle', 'showRotateControl'],
        item: {
          children: [],
          itemId: 'googleMap',
          parent: 'flexItem',
          props: {
            zoomOnScrollWheel: false,
            tiltAngle: 47.5,
            showRotateControl: true,
            referenceLatitude: 37.7893719,
            referenceLongitude: -122.3942,
            defaultZoomLevel: 16,
            mapType: 'satellite',
            showStreetViewControl: false,
          },
          type: 'GoogleMap',
        },
      },
    ],
  };
};

export default GoogleMapManager;
